/**
 * Mission 16's hilltop: the 12 m relay mast and the generator hut that feeds it (and, through a
 * blackout group, every searchlight on the map). Both have a lamp that is a separate self-lit part
 * (`relayMastLamp`, `generatorLamp`) so the world can make it glow, and put it out.
 */

import type { Palette } from '../palette';
import { MIN_BAR_METRES } from './budgets';
import type { KitBuilder } from './flora';
import { MeshBuilder, place } from './MeshBuilder';
import { sided, strut, tubeZ } from './parts';
import { capColor, snowSlab } from './snow';

const MAST = { foot: 0.25, height: 11.2, base: 0.55, tip: 0.16, bays: 6 };
/** Guy anchors stand this far out on the four diagonals; two wires run to each, from these heights. */
const GUY_RADIUS = 3.3;
const GUY_HEIGHTS = [6.0, 10.4];
/**
 * KIT3 flagged the guys at 6 cm - one pixel - and nobody had looked. At 18 px/m a 6 cm wire is a
 * string of dots that crawls as the camera scrolls, and the lattice braces at 9 cm were no better.
 * Every bar of the mast now stands at the spec's own floor (`MIN_BAR_METRES`, 05 §5 rule 3), which
 * costs not one triangle; the 4 cm whip above the lamp, which could not be saved, is a square bar.
 */
const BAR = MIN_BAR_METRES;
/** Centre of the aviation lamp: the body draws it as a red block, the glow part just proud of it. */
const MAST_LAMP_Y = MAST.foot + MAST.height + 0.42;

const halfAt = (y: number): number => MAST.base - ((MAST.base - MAST.tip) * y) / MAST.height;

/**
 * The mast without its guys: what stands on the 0.9 m circle the sim collides with. Four tapering
 * legs, red and white collars (the bands that pick it out on the flyover), zig-zag bracing on the
 * south and north faces - the other two are edge-on to the fixed camera - and two relay drums.
 */
export const relayMastCore: KitBuilder = (p) => {
  const mb = new MeshBuilder();
  mb.box(1.7, MAST.foot, 1.7, { all: p.color('concrete.lo'), top: capColor(p, 'concrete.mid') });
  mb.with(place(0, MAST.foot, 0), () => {
    for (const sx of [-1, 1]) {
      for (const sz of [-1, 1]) {
        const foot = [sx * MAST.base, 0, sz * MAST.base] as const;
        strut(mb, foot, [sx * MAST.tip, MAST.height, sz * MAST.tip], BAR, p.color('metal.lo'));
      }
    }
    for (let i = 1; i <= MAST.bays; i++) {
      const y0 = (MAST.height * (i - 1)) / MAST.bays;
      const y1 = (MAST.height * i) / MAST.bays;
      const w = halfAt(y1) * 2 + 0.2;
      mb.box(w, 0.16, w, p.color(i % 2 ? 'enemyAccent.0' : 'paper.0'), place(0, y1 - 0.08, 0));
      const dir = i % 2 ? 1 : -1;
      for (const sz of [-1, 1]) {
        const from = [-dir * halfAt(y0), y0, sz * halfAt(y0)] as const;
        strut(mb, from, [dir * halfAt(y1), y1, sz * halfAt(y1)], BAR, p.color('metal.mid'));
      }
    }
  });
  // Relay drums, their pale faces turned a little east and west of south.
  const drum = (x: number, y: number, yaw: number): void => {
    const face = p.color('paper.0');
    mb.with(place(x, y, 0.3, yaw), () => tubeZ(mb, 0, 0, 0, 0.3, 0.46, 0.46, 8, p.color('metal.mid'), face));
  };
  drum(0.12, 9.6, 0.5);
  drum(-0.1, 8.3, -0.6);
  // Spike, the aviation lamp, and a whip above it.
  mb.box(BAR, 0.3, BAR, p.color('metal.lo'), place(0, MAST.foot + MAST.height, 0));
  mb.box(0.26, 0.26, 0.26, p.color('fire.0'), place(0, MAST_LAMP_Y - 0.13, 0));
  mb.box(BAR, 1.0, BAR, p.color('metal.lo'), place(0, MAST_LAMP_Y + 0.13, 0));
  return mb;
};

/** The relay mast (mission 16): the core, plus guy wires out to four anchor blocks on the diagonals. */
export const relayMast: KitBuilder = (p, rng) => {
  const mb = relayMastCore(p, rng);
  const out = GUY_RADIUS / Math.SQRT2;
  for (const sx of [-1, 1]) {
    for (const sz of [-1, 1]) {
      const block = { all: p.color('concrete.lo'), top: capColor(p, 'concrete.mid') };
      mb.box(0.5, 0.3, 0.5, block, place(sx * out, 0, sz * out));
      for (const y of GUY_HEIGHTS) {
        const k = halfAt(y - MAST.foot);
        strut(mb, [sx * out, 0.25, sz * out], [sx * k, y, sz * k], BAR, p.color('ink.1'));
      }
    }
  }
  return mb;
};

/** The aviation lamp alone, a shade larger than the block in the body: drawn self-lit by the world. */
export const relayMastLamp: KitBuilder = (p) => {
  const mb = new MeshBuilder();
  mb.box(0.3, 0.3, 0.3, p.color('fire.0'), place(0, MAST_LAMP_Y - 0.15, 0));
  return mb;
};

/* ------------------------------------------------------------------------------ generator hut */

const HUT_X = -0.5;
const HUT_FRONT = 1.25;
const LAMP = { x: HUT_X + 0.7, y: 1.74, z: HUT_FRONT + 0.06 };

function generator(p: Palette, running: boolean): MeshBuilder {
  const mb = new MeshBuilder();
  // Corrugated steel on a slab, flat roof: a machine shed, not somewhere men sleep.
  mb.box(2.5, 2.0, 2.5, sided(p, 'metal'), place(HUT_X, 0, 0));
  mb.box(2.9, 0.2, 2.9, { all: p.color('roof.0'), top: p.color('roof.1') }, place(HUT_X, 2.0, 0));
  mb.with(place(HUT_X, 0, 0), () => snowSlab(mb, p, 2.9, 2.9, 2.2, 0.28));
  // Exhaust stack with a rain cap: the tallest thing about it.
  mb.cylinder(0.14, 0.14, 1.0, 6, p.color('ink.1'), place(HUT_X - 0.8, 2.2, -0.8));
  mb.cylinder(0.3, 0.1, 0.16, 6, p.color('metal.lo'), place(HUT_X - 0.8, 3.24, -0.8));
  // South face: louvres, door, the running lamp over it and the cut-out switch beside it.
  mb.box(1.1, 0.85, 0.06, p.color('ink.0'), place(HUT_X - 0.55, 0.85, HUT_FRONT + 0.01));
  const slat = p.color('metal.hi');
  for (const y of [0.95, 1.22, 1.49]) mb.box(1.1, 0.09, 0.12, slat, place(HUT_X - 0.55, y, HUT_FRONT + 0.03));
  mb.box(0.8, 1.6, 0.06, p.color('ink.1'), place(LAMP.x, 0, HUT_FRONT + 0.01));
  mb.box(0.24, 0.2, 0.12, p.color(running ? 'fire.2' : 'ink.1'), place(LAMP.x, LAMP.y - 0.1, LAMP.z));
  mb.box(0.16, 0.34, 0.08, p.color('metal.lo'), place(HUT_X + 0.12, 1.0, HUT_FRONT + 0.04));
  const handle = running ? 1.36 : 0.98;
  const grip = p.color('playerAccent.0');
  strut(mb, [HUT_X + 0.12, 1.17, HUT_FRONT + 0.08], [HUT_X + 0.12, handle, HUT_FRONT + 0.24], 0.07, grip);
  // Fuel drum on a cradle against the east wall, with its feed pipe: the reason it burns so well.
  for (const z of [-0.45, 0.55]) mb.box(0.7, 0.4, 0.2, p.color('timber.lo'), place(1.3, 0, z));
  tubeZ(mb, 1.3, 0.8, -0.75, 0.85, 0.42, 0.42, 8, p.color('enemyAccent.0'), p.color('metal.lo'));
  mb.box(0.6, 0.08, 0.08, p.color('metal.lo'), place(0.85, 0.5, 0));
  return mb;
}

/** Generator hut, running: lamp lit, switch up. */
export const generatorHut: KitBuilder = (p) => generator(p, true);
/** The same hut with the switch thrown: lamp dark, handle down. */
export const generatorHutOff: KitBuilder = (p) => generator(p, false);

/** The running lamp over the door, a shade proud of the body's: the self-lit part. */
export const generatorLamp: KitBuilder = (p) => {
  const mb = new MeshBuilder();
  mb.box(0.28, 0.24, 0.12, p.color('fire.2'), place(LAMP.x, LAMP.y - 0.12, LAMP.z + 0.02));
  return mb;
};
